import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from 'react';
import noop from 'lodash/noop';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ButtonGroup } from '@atlaskit/button';
import Button, { IconButton, LinkButton } from '@atlaskit/button/new';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import Link from '@atlaskit/link';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';
import { R300 } from '@atlaskit/theme/colors';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { FORM_RESTRICTION_URL } from '../../../../common/constants';
import { LoadingModalBodyContent, LoadingModalBodyDescription } from '../../../../common/ui/loading-state';
import { PrincipalList } from '../../../../common/ui/principal-list';
import { useFormPermissions } from '../../../../controllers/form-permissions';
import { useLoadRestrictedPrincipals } from '../../../../services/load-restricted-principals';
import { messages } from './messages';
export const ViewConfigModal = ({
  isOpen,
  onClose,
  getWarningMessage,
  viewModeManageRestrictionUrl
}) => {
  const [{
    principals,
    loadRestrictedPrincipals: loadFn
  }, {
    setPrincipals
  }] = useFormPermissions();
  const {
    loading,
    error,
    loadRestrictedPrincipals
  } = useLoadRestrictedPrincipals(loadFn);
  const {
    formatMessage
  } = useIntl();
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  const [warningMessage, setWarningMessage] = useState();
  const [isLoadingWarningMessage, setIsLoadingWarningMessage] = useState(false);
  useEffect(() => {
    if (isOpen) {
      loadRestrictedPrincipals().then(data => {
        var _data$principals;
        setPrincipals((_data$principals = data === null || data === void 0 ? void 0 : data.principals) !== null && _data$principals !== void 0 ? _data$principals : []);
        fireUIAnalytics(createAnalyticsEvent({
          actionSubject: ActionSubject.MODAL,
          action: data ? Action.VIEWED : Action.FAILED
        }), ActionSubject.FORM_PERMISSIONS, {
          isViewMode: true
        });
      });
    }
  }, [createAnalyticsEvent, isOpen, loadRestrictedPrincipals, setPrincipals]);
  useEffect(() => {
    const fetchWarningMessage = async () => {
      if (isOpen && getWarningMessage) {
        setIsLoadingWarningMessage(true);
        const message = await getWarningMessage();
        setWarningMessage(message);
      }
      setIsLoadingWarningMessage(false);
    };
    if (
    // eslint-disable-next-line @atlaskit/platform/no-preconditioning
    !fg('proforma_granular_permissions_analytic_attributes')) {
      fetchWarningMessage();
    }
  }, [isOpen, getWarningMessage]);
  const modalHeader = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ModalTitle, null, /*#__PURE__*/React.createElement(FormattedMessage, messages.title)), /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    spacing: "compact",
    icon: CrossIcon,
    label: formatMessage(messages.restrictionsModalCloseIconLabel),
    onClick: onClose
  }));
  const modalBodyDescription = loading ? /*#__PURE__*/React.createElement(LoadingModalBodyDescription, null) : /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.description, {
    values: {
      link: chunks => /*#__PURE__*/React.createElement(Link, {
        href: FORM_RESTRICTION_URL,
        target: "_blank"
      }, chunks)
    }
  }));
  const modalBodyWarning = warningMessage && loading ? null : /*#__PURE__*/React.createElement(SectionMessage, {
    title: "",
    appearance: "warning"
  }, /*#__PURE__*/React.createElement(Text, null, warningMessage));
  const getModalBodyContent = () => {
    if (loading || isLoadingWarningMessage) {
      return /*#__PURE__*/React.createElement(LoadingModalBodyContent, null);
    }
    if (error) {
      return /*#__PURE__*/React.createElement(Inline, {
        space: "space.050",
        alignBlock: "center"
      }, /*#__PURE__*/React.createElement(ErrorIcon, {
        spacing: "spacious",
        label: "load-error",
        color: `var(--ds-icon-danger, ${R300})`
      }), /*#__PURE__*/React.createElement(FormattedMessage, messages.restrictionsLoadFailureDescription));
    }
    return /*#__PURE__*/React.createElement(PrincipalList, {
      principals: principals,
      isViewMode: true,
      removePrincipal: noop
    });
  };
  const modalFooter = viewModeManageRestrictionUrl ? /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(LinkButton, {
    href: viewModeManageRestrictionUrl,
    target: "_blank",
    iconAfter: iconProps => /*#__PURE__*/React.createElement(LinkExternalIcon, _extends({}, iconProps, {
      size: "small"
    })),
    "aria-label": formatMessage(messages.manageButtonLabel)
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.manageButton)), /*#__PURE__*/React.createElement(Button, {
    appearance: "primary",
    onClick: onClose,
    "aria-label": formatMessage(messages.closeButtonLabel)
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.closeButton))) : /*#__PURE__*/React.createElement(Button, {
    appearance: "primary",
    onClick: onClose
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.closeButton));
  return /*#__PURE__*/React.createElement(ModalTransition, null, isOpen && /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose,
    height: 500
  }, /*#__PURE__*/React.createElement(ModalHeader, null, modalHeader), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Stack, {
    space: "space.300"
  }, /*#__PURE__*/React.createElement(Box, null, modalBodyDescription), modalBodyWarning && warningMessage && !fg('proforma_granular_permissions_analytic_attributes') && /*#__PURE__*/React.createElement(Box, null, modalBodyWarning), /*#__PURE__*/React.createElement(Box, null, getModalBodyContent()))), /*#__PURE__*/React.createElement(ModalFooter, null, loading ? null : modalFooter)));
};